import type { DragEndEvent } from '@dnd-kit/core';
import type { Key } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { arrayMove } from '@dnd-kit/sortable';
import { Button, ConfigProvider, Form, type FormInstance } from 'antd';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type { ProdutoComposicaoBody } from '@/features/produto/types/ProdutoComposicao';
import { TableDrag } from '@/components/table/drag/TableDrag';
import { findItemAndIndex } from '@/std/array';
import { useDialog } from '@/std/dialog/useDialog';
import { roundAbnt } from '@/std/math';
import { getColumnsKit } from './getColumnsKit';
import { ProdutoTabComposicaoKitModal } from './ProdutoTabComposicaoKitModal';
import { ProdutoTabComposicaoKitSummary } from './ProdutoTabComposicaoKitSummary';

export function ProdutoTabComposicaoKit({
    form,
    setFormDirty,
}: {
    form: FormInstance<FormProdutoValues>;
    setFormDirty: (dirty: boolean) => void;
}) {
    const dialog = useDialog<ProdutoComposicaoBody>();

    const produtoComposicaoForm = Form.useWatch('produto_composicao', form);
    const produtoComposicao = produtoComposicaoForm?.filter((item) => item.excluir !== 'S') ?? [];

    const recalcularPercentuais = (): void => {
        const precoVenda: number = form.getFieldValue('pro_preco_venda') ?? 0;

        const produtoComposicao: ProdutoComposicaoBody[] =
            form.getFieldValue('produto_composicao') || [];

        const newProdutoComposicao = produtoComposicao.map((item) => {
            if (item.excluir === 'S') {
                return item;
            }

            // Arredonda para duas casas decimais para evitar problemas de precisão
            // ex: 74,9999 vai virar 75,00
            const oldTotalRepresentado = roundAbnt(item.totalItemKit ?? 0, 2);

            const percentual =
                precoVenda > 0 ? roundAbnt((oldTotalRepresentado / precoVenda) * 100, 4) : 100;

            const newTotalRepresentado = roundAbnt((precoVenda * percentual) / 100, 4);

            return {
                ...item,
                prc_rateio_percentual: percentual,
                totalItemKit: newTotalRepresentado,
            };
        });

        form.setFieldValue('produto_composicao', newProdutoComposicao);
    };

    const recalcularPrecoCusto = (): void => {
        const produtoComposicao: ProdutoComposicaoBody[] =
            form.getFieldValue('produto_composicao') || [];

        const precoCusto = produtoComposicao.reduce((acc, item) => {
            if (item.excluir === 'S') {
                return acc;
            }

            const precoCustoProduto = item.produto?.pro_preco_custo ?? 0;
            const quantidade = item.prc_quantidade ?? 0;
            return acc + precoCustoProduto * quantidade;
        }, 0);

        form.setFieldValue('pro_preco_custo', roundAbnt(precoCusto, 4));
    };

    const recalcularOrdem = (): void => {
        const produtoComposicao: ProdutoComposicaoBody[] =
            form.getFieldValue('produto_composicao') || [];

        let ordem = 0;

        const newProdutoComposicao = produtoComposicao.map((item) => {
            if (item.excluir === 'S') {
                return item;
            }

            ordem += 1;

            return {
                ...item,
                prc_ordem: ordem,
            };
        });

        form.setFieldValue('produto_composicao', newProdutoComposicao);
    };

    const onDelete = (key: Key): void => {
        const produtoComposicaoForm: ProdutoComposicaoBody[] =
            form.getFieldValue('produto_composicao');

        const found = findItemAndIndex(produtoComposicaoForm, (i) => i.key === key);
        if (!found) {
            return;
        }

        if (found.item.prc_idpk) {
            form.setFieldValue(['produto_composicao', found.index], {
                excluir: 'S',
                key: found.item.key,
                prc_idpk: found.item.prc_idpk,
            });
        } else {
            const produtoComposicaoForm = form.getFieldValue('produto_composicao');
            const newData = [...(produtoComposicaoForm ?? [])];
            newData.splice(found.index, 1);
            form.setFieldValue('produto_composicao', newData);
        }

        const precoVenda = form.getFieldValue('pro_preco_venda') ?? 0;
        const totalItemKit = found.item.excluir === 'S' ? 0 : (found.item.totalItemKit ?? 0);
        const newPrecoVenda = roundAbnt(precoVenda - totalItemKit, 2);
        form.setFieldValue('pro_preco_venda', newPrecoVenda);
        recalcularPercentuais();
        recalcularPrecoCusto();
        recalcularOrdem();
        setFormDirty(true);
    };

    const columns = getColumnsKit({ form, onDelete, recalcularPrecoCusto, setFormDirty });

    const onDragEnd = ({ active, over }: DragEndEvent): void => {
        if (!over || active.id === over.id) {
            return;
        }

        const ativos = (produtoComposicaoForm ?? []).filter((i) => i.excluir !== 'S');
        const excluidos = (produtoComposicaoForm ?? []).filter((i) => i.excluir === 'S');

        const activeIndex = ativos.findIndex((i) => i.key === active.id);
        const overIndex = ativos.findIndex((i) => i.key === over.id);

        if (activeIndex < 0 || overIndex < 0) {
            return;
        }

        const newData = arrayMove(ativos, activeIndex, overIndex).map((item, index) => ({
            ...item,
            prc_ordem: index + 1,
        }));

        form.setFieldValue('produto_composicao', [...newData, ...excluidos]);
        setFormDirty(true);
    };

    return (
        <div>
            <Form.Item
                hidden={true}
                name='produto_composicao'
                rules={[
                    {
                        validator(_, value) {
                            const itensAtivos = value?.filter(
                                (item: ProdutoComposicaoBody) => item.excluir !== 'S',
                            );

                            if (!itensAtivos || itensAtivos.length === 0) {
                                return Promise.reject(
                                    'A composição do kit deve conter pelo menos um produto.',
                                );
                            }

                            const somaPercentual = value.reduce(
                                (acc: number, item: ProdutoComposicaoBody) => {
                                    if (item.excluir === 'S') {
                                        return acc;
                                    }

                                    return acc + (item.prc_rateio_percentual ?? 0);
                                },
                                0,
                            );

                            const somaArredondada = Math.round(somaPercentual * 100) / 100;

                            if (Math.abs(somaArredondada - 100) > 0.01) {
                                return Promise.reject(
                                    new Error(
                                        'A soma do percentual de rateio deve ser igual a 100%.',
                                    ),
                                );
                            }

                            return Promise.resolve();
                        },
                    },
                ]}
            />

            <ConfigProvider
                theme={{
                    components: {
                        Table: {
                            borderColor: '#d1d5db', // gray-300
                        },
                    },
                }}
            >
                <TableDrag
                    columns={columns}
                    dataSource={produtoComposicao}
                    onDragEnd={onDragEnd}
                    pagination={false}
                    scroll={{ x: 1000 }}
                    summary={() => (
                        <ProdutoTabComposicaoKitSummary
                            form={form}
                            produtoComposicao={produtoComposicao}
                            setFormDirty={setFormDirty}
                        />
                    )}
                />
            </ConfigProvider>

            <div className='mt-4 flex justify-center gap-2'>
                <Button
                    icon={<PlusOutlined />}
                    onClick={() => {
                        dialog.actions.add();
                    }}
                    type='primary'
                >
                    Adicionar produto
                </Button>
            </div>

            <ProdutoTabComposicaoKitModal
                dialog={dialog}
                form={form}
                recalcularPercentuais={recalcularPercentuais}
                recalcularPrecoCusto={recalcularPrecoCusto}
                setFormDirty={setFormDirty}
            />
        </div>
    );
}
